import { memo, MouseEventHandler, useCallback } from 'react';
import { ButtonProps } from 'antd';
import { useAdditionalFormContext } from '../../AdditionalFormContext';
import { useFocusTableRowField } from './useFocusTableRowField';
import { CellContext } from '@tanstack/react-table';
import { useFormContext } from 'react-hook-form';
import { useFormTableName } from './TanstackFormTable';
import { CloneTableRowButton } from './CloneTableRowButton';
import { useAppendFormTableRow } from './useAppendFormTableRow';

export interface CloneLastFormTableRowButtonProps
  extends Omit<ButtonProps, 'id'> {
  id?: string;
  context: CellContext<any, any>;
  resetId?: boolean;
  resetValues?: any;
  autoFocusField?: string;
}

export const CloneLastFormTableRowButton = memo(
  (props: CloneLastFormTableRowButtonProps) => {
    const { context, resetId, resetValues, autoFocusField, ...rest } = props;
    const { getValues } = useFormContext();
    const tableName = useFormTableName();
    const { onChange: formOnChange } = useAdditionalFormContext();

    const focusField = useFocusTableRowField(autoFocusField);
    const appendRow = useAppendFormTableRow();
    const handleClick: MouseEventHandler<HTMLButtonElement> =
      useCallback(() => {
        const reset = resetId ? { id: null } : {};
        appendRow({
          ...getValues(`${tableName}.${context.row.index}`),
          ...reset,
          ...resetValues,
        });
        focusField();
        formOnChange?.();
      }, [
        resetId,
        appendRow,
        getValues,
        tableName,
        context.row.index,
        resetValues,
        focusField,
        formOnChange,
      ]);

    return (
      <CloneTableRowButton context={context} onClick={handleClick} {...rest} />
    );
  }
);
